import dayjs from 'dayjs';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import GroupMembersIcon from '@/assets/images/common/svg/group-members-icon.svg?react';
import { messageApis } from '@/configs/apis/chat/messages';
import type { UserCardResponse, UserGroupRelationResponse } from '@/configs/apis/user';
import { userApis } from '@/configs/apis/user';
import { getAvatar } from '@/scripts/utils/genAvatar';
import styles from './index.module.scss';
import MoreIcon from './images/more-icon.svg?react';
import TipIcon from './images/tip-icon.svg?react';

interface SysAlertProps {
  chat: Chat;
  isFriend?: boolean;
}

function SysAlert({ chat, isFriend }: SysAlertProps) {
  const [haveMsgSended, setHaveMsgSended] = useState(false);

  const [userCard, setUserCard] = useState<UserCardResponse | null>(null);
  const [userGroups, setUserGroups] = useState<UserGroupRelationResponse['groups']>([]);

  const isSingle = useMemo(
    () => chat.category === ChatCategory.SINGLE,
    [chat.category]
  );

  const isShow = useMemo(() => {
    return isSingle && !isFriend && !haveMsgSended;
  }, [isSingle, isFriend, haveMsgSended]);

  const getApi = useCallback(async () => {
    const cardRes = await userApis.getUserCard(chat.user.userId);
    const groupsRes = await userApis.getUserGroupRelation(chat.user.userId);
    setUserCard(cardRes?.data);
    setUserGroups(groupsRes?.data?.groups || []);
  }, [chat.user.userId]);

  useEffect(() => {
    if (isSingle && !isFriend) {
      messageApis.queryHasSentMessages(chat.chat_id).then(async (bool) => {
        setHaveMsgSended(bool);
      });
    }
    // must be chat, not chat.chat_id
  }, [isSingle, isFriend, chat]);

  useEffect(() => {
    if (isSingle && !isFriend && !haveMsgSended) {
      getApi();
    }
  }, [haveMsgSended, getApi, isSingle, isFriend]);

  return (
    <div className={cls(styles.sysAlert, isShow && styles.show)}>
      <div className={styles.title}>{chat.name}</div>
      <div className={styles.subTitle}>not a contact</div>
      <div className={styles.list}>
        <div className={styles.item}>
          <div className={styles.label}>Registration</div>
          <div className={styles.value}>
            {dayjs(userCard?.register_time).format('MMM YYYY')}
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.label}>Region</div>
          <div className={styles.value}>{userCard?.country || '- -'}</div>
        </div>
        <div className={styles.item}>
          <div className={styles.label}>Mutual Group</div>
          <Popover
            trigger="click"
            arrow={false}
            classNames={{
              root: styles.myPopover,
              body: styles.popoverBody,
            }}
            content={
              <div className={styles.popoverContent}>
                <div className={styles.title}>
                  <span className={styles.number}>{userCard?.groups_num}</span>
                  <span>Mutual Groups</span>
                </div>
                <div className={cls(styles.groups, 'scrollbar_hover')}>
                  {userGroups.map((group) => (
                    <div className={styles.group} key={group.gid}>
                      <MyAvatar
                        className={styles.memberAvatar}
                        src={getAvatar(group.group_head, group.group_name)}
                        category={ChatCategory.GROUP}
                      />
                      <div className={styles.groupName}>
                        <GroupMembersIcon />
                        <div className={styles.groupNameText}>{group.group_name}</div>
                        <div className={styles.groupMembers}>
                          ({group.member_num || '- -'})
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <div className={cls(styles.value, 'pointer')}>
              <div className={styles.commonGroup}>{userCard?.groups_num} Groups</div>
              <div className={styles.commonGroupMembers}>
                {userGroups.slice(0, 3).map((group) => (
                  <MyAvatar
                    className={styles.memberAvatar}
                    src={getAvatar(group.group_head, group.group_name)}
                    key={group.gid}
                    category={ChatCategory.GROUP}
                  />
                ))}
              </div>
              <MoreIcon />
            </div>
          </Popover>
        </div>
      </div>
      {!userCard?.is_official && (
        <div className={styles.tip}>
          <TipIcon />
          <div>Not an Official Account</div>
        </div>
      )}
    </div>
  );
}

export default SysAlert;
